import { useState } from 'react'
import { Bell, Check, Clock3 } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'

const NOTIFICATION_DATE_FORMATTER = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
})

function notificationDate(value) {
  if (!value) return 'Recently'

  return NOTIFICATION_DATE_FORMATTER.format(new Date(value))
}

export function ListingNotificationsDialog({
  error,
  isLoading,
  notifications,
  onOpenChange,
  onView,
  open,
  unreadCount,
}) {
  const [pendingId, setPendingId] = useState(null)
  const [viewError, setViewError] = useState(null)
  const notificationLabel = unreadCount > 0
    ? `Notifications, ${unreadCount} unread`
    : 'Notifications'

  const handleView = async (notification) => {
    setPendingId(notification.id)
    setViewError(null)
    try {
      await onView(notification.id)
    } catch (viewFailure) {
      setViewError(viewFailure.message)
    } finally {
      setPendingId(null)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label={notificationLabel}
        onClick={() => onOpenChange(true)}
        className="relative text-muted-foreground hover:text-foreground"
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 ? (
          <Badge className="absolute -right-2 -top-2 min-w-5 px-1 text-[10px]">
            {unreadCount > 9 ? '9+' : unreadCount}
          </Badge>
        ) : null}
      </Button>

      <DialogContent className="gap-0 overflow-hidden p-0 sm:max-w-xl">
        <DialogHeader className="border-b border-border p-5 pr-12 text-left">
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-primary" />
            <DialogTitle>Listing updates</DialogTitle>
          </div>
          <DialogDescription>
            Price and status changes for properties in your live pipeline.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="min-w-0 max-h-[min(32rem,70vh)] [&_[data-slot=scroll-area-viewport]>div]:block!">
          <div className="space-y-3 p-4">
            {isLoading ? (
              <p className="py-8 text-center text-sm text-muted-foreground">Loading updates…</p>
            ) : null}
            {!isLoading && notifications.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">No listing updates yet.</p>
            ) : null}
            {!isLoading ? notifications.map((notification) => (
              <article
                key={notification.id}
                className={cn(
                  'rounded-lg border border-border p-4',
                  notification.unread ? 'bg-primary/5' : 'bg-card',
                )}
              >
                <div className="flex items-start gap-3">
                  <span className={cn(
                    'mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border',
                    notification.unread
                      ? 'border-primary/20 bg-primary/10 text-primary'
                      : 'border-border bg-muted text-muted-foreground',
                  )}>
                    {notification.unread ? <Clock3 className="h-4 w-4" /> : <Check className="h-4 w-4" />}
                  </span>
                  <div className="min-w-0 flex-1 space-y-2">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <h3 className="text-sm font-bold text-foreground">{notification.title}</h3>
                      <span className="text-xs text-muted-foreground">
                        {notificationDate(notification.createdAt)}
                      </span>
                    </div>
                    <p className="text-sm leading-6 text-muted-foreground">{notification.message}</p>
                    <div className="flex items-center justify-between gap-3">
                      <span className="truncate text-xs text-muted-foreground">
                        {notification.dealAddress}
                      </span>
                      <Button
                        type="button"
                        size="sm"
                        disabled={pendingId === notification.id}
                        onClick={() => handleView(notification)}
                        aria-label={`View change for ${notification.title}`}
                      >
                        {pendingId === notification.id ? 'Opening…' : 'View change'}
                      </Button>
                    </div>
                  </div>
                </div>
              </article>
            )) : null}
          </div>
        </ScrollArea>

        {viewError || error ? (
          <p className="border-t border-border px-5 py-3 text-sm text-destructive">
            {viewError || error}
          </p>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
